import { useEffect, useRef, useState, type RefObject } from "react"

export interface FocusWithinOptions {
  /** Called when focus enters or leaves the element, with whether it is now inside. */
  readonly onChange?: (focusWithin: boolean) => void
  /**
   * A ref the caller owns, for example in a provider above the element, that always holds whether focus is inside.
   * When the element mounts while it says yes, focus returns to the element, so a panel that is remounted keeps the
   * keyboard focus. The element has to be focusable, for example with `tabIndex={-1}`.
   */
  readonly retain?: { current: boolean }
}

/**
 * Whether keyboard focus is inside `target` (the element itself or a DOM descendant). Re-renders when that changes.
 *
 * Focus lost because the focused element was removed does not count as the user leaving: the answer stays as it was
 * until focus moves somewhere else, which suits panels whose rows or fields come and go.
 */
export function useFocusWithin(target: RefObject<HTMLElement | null>, options: FocusWithinOptions = {}): boolean {
  const [focusWithin, setFocusWithin] = useState(false)
  const current = useRef(false)
  const onChangeRef = useRef(options.onChange)
  onChangeRef.current = options.onChange
  const retain = options.retain

  useEffect(() => {
    const update = (next: boolean) => {
      if (current.current === next) return
      current.current = next
      if (retain !== undefined) retain.current = next
      setFocusWithin(next)
      onChangeRef.current?.(next)
    }
    const inside = (node: EventTarget | null) =>
      node instanceof Node && target.current !== null && target.current.contains(node)
    const onFocusIn = (event: FocusEvent) => update(inside(event.target))
    const onFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget !== null || !(event.target instanceof Element)) return
      const lost = event.target
      // A removed element loses focus without the user leaving. Browsers dispatch the event before the element is
      // gone, so whether it is still in the document is only known once the removal has finished.
      queueMicrotask(() => {
        if (lost.isConnected) update(false)
      })
    }
    if (retain?.current === true) target.current?.focus({ preventScroll: true })
    update(inside(document.activeElement))
    document.addEventListener("focusin", onFocusIn)
    document.addEventListener("focusout", onFocusOut)
    return () => {
      document.removeEventListener("focusin", onFocusIn)
      document.removeEventListener("focusout", onFocusOut)
    }
  }, [target, retain])

  return focusWithin
}
